/**
 * MERODE edge-marker ids and id maps — the pure-data layer behind `markers.tsx`.
 *
 * Split out of `markers.tsx` so that the component file exports only components:
 * `MARKER_VARIANTS` / `ALL_MARKER_IDS` are computed (non-literal) values, and a
 * non-component export in a `.tsx` file breaks Vite/react-refresh Fast Refresh.
 * Every id here matches a JointJS marker id 1:1 (see `src/lib/joint/markers.js`).
 */

// --- ids (mirror src/lib/joint/markers.js) ---------------------------------

export const OPTIONAL = 'merode-marker-optional';
export const MANDATORY = 'merode-marker-mandatory';
export const MANY = 'merode-marker-many';
export const INHERIT = 'merode-marker-inherit';
export const INHERIT_ABSTRACT = 'merode-marker-inherit-abstract';

export type VariantKey = 'HL' | 'HLP' | 'PRIMARY' | 'SECONDARY';

export const VARIANTS: Record<VariantKey, { suffix: string; color: string }> = {
  HL: { suffix: 'highlighted', color: '#FFAC2C' },
  HLP: { suffix: 'highlighted-path', color: '#DC2376' },
  PRIMARY: { suffix: 'primary-path', color: '#34d18a' },
  SECONDARY: { suffix: 'secondary-path', color: '#f85359' },
};

export type MarkerNames = {
  OPTIONAL: string;
  MANDATORY: string;
  MANY: string;
  INHERIT: string;
  INHERIT_ABSTRACT: string;
};

export const MARKER_VARIANTS: Record<VariantKey, MarkerNames> = Object.fromEntries(
  (Object.entries(VARIANTS) as [VariantKey, { suffix: string }][]).map(([key, v]) => [
    key,
    {
      OPTIONAL: `merode-marker-optional-${v.suffix}`,
      MANDATORY: `merode-marker-mandatory-${v.suffix}`,
      MANY: `merode-marker-many-${v.suffix}`,
      INHERIT: `merode-marker-inherit-${v.suffix}`,
      INHERIT_ABSTRACT: `merode-marker-inherit-abstract-${v.suffix}`,
    },
  ])
) as Record<VariantKey, MarkerNames>;

/** Flat list of all 25 marker ids (5 base + 5*4 variants), in definition order. */
export const ALL_MARKER_IDS: string[] = [
  OPTIONAL,
  MANDATORY,
  MANY,
  INHERIT,
  INHERIT_ABSTRACT,
  ...(Object.values(MARKER_VARIANTS).flatMap((v) => [
    v.OPTIONAL,
    v.MANDATORY,
    v.MANY,
    v.INHERIT,
    v.INHERIT_ABSTRACT,
  ]) as string[]),
];
